import { type MouseEvent, type ReactNode, useEffect, useState, useSyncExternalStore } from "react";
import { portalFetch, setPortalUnauthorizedHandler } from "./api/client";
import { portalAuthErrorMessage } from "./auth/errors";
import { addPortalPasskey, logoutPortal } from "./auth/passkeys";
import { clearPortalActor, getPortalActor, setPortalActor, subscribePortalActor } from "./auth/session";
import { InboxView } from "./components/InboxView";
import { InvitationView } from "./components/InvitationView";
import { LoginView } from "./components/LoginView";
import { OrganizationInvitationView } from "./components/OrganizationInvitationView";
import { OrganizationView } from "./components/OrganizationView";
import { ProjectDetailView } from "./components/ProjectDetailView";
import { ProjectListView } from "./components/ProjectListView";
import { ProposalView } from "./components/ProposalView";
import type { PortalMeDto } from "./model";
import { navigatePortal, type PortalRoute, parsePortalRoute } from "./router";

function PortalLink({ href, active, children }: { href: string; active: boolean; children: ReactNode }) {
	function navigate(event: MouseEvent<HTMLAnchorElement>) {
		if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
		event.preventDefault();
		navigatePortal(href);
	}
	return (
		<a
			href={href}
			onClick={navigate}
			className={active ? "active" : undefined}
			aria-current={active ? "page" : undefined}
		>
			{children}
		</a>
	);
}

function AccountMenu({ actor, onSignOut }: { actor: PortalMeDto; onSignOut: () => Promise<void> }) {
	const [adding, setAdding] = useState(false);
	const [feedback, setFeedback] = useState<{ kind: "success" | "error"; message: string }>();

	async function addPasskey() {
		if (adding) return;
		setAdding(true);
		setFeedback(undefined);
		try {
			await addPortalPasskey();
			setFeedback({
				kind: "success",
				message: "Passkey added. You can use it the next time you sign in.",
			});
		} catch (error) {
			setFeedback({
				kind: "error",
				message: portalAuthErrorMessage(error, "Passkey wasn’t added. Nothing changed."),
			});
		} finally {
			setAdding(false);
		}
	}

	return (
		<details className="rail-account">
			<summary role="button" aria-label="Account menu">
				<span className="rail-account-label">Account</span>
				<span className="rail-account-compact" aria-hidden="true">
					•••
				</span>
			</summary>
			<div className="rail-account-menu">
				<span>Signed in as</span>
				<strong>{actor.displayName}</strong>
				<div className="rail-passkey-setup">
					<span>ADD A PHONE</span>
					<p>
						Confirm your passkey, then choose <strong>Use another device</strong> and scan the QR with your phone.
					</p>
					<button
						type="button"
						className="account-add-passkey"
						disabled={adding}
						onClick={() => void addPasskey()}
					>
						{adding ? "Opening passkey setup…" : "Add passkey"}
					</button>
					{feedback && (
						<p className="rail-account-feedback" data-kind={feedback.kind} role="status">
							{feedback.message}
						</p>
					)}
				</div>
				<button type="button" className="account-sign-out" onClick={() => void onSignOut()}>
					Sign out…
				</button>
			</div>
		</details>
	);
}

function AuthShell({ children }: { children: ReactNode }) {
	return (
		<main className="auth-shell">
			<section className="auth-intro" aria-label="Substrate Collaboration Portal">
				<div className="brand-lockup brand-lockup-light">
					<span className="brand-sigil" aria-hidden="true">
						S
					</span>
					<span>
						Substrate
						<small>Collaboration Portal</small>
					</span>
				</div>
				<div className="auth-intro-copy">
					<p className="eyebrow">A SHARED PLACE TO SHAPE THE WORK</p>
					<h2>Decisions stay clear. Progress stays visible.</h2>
					<p>Review direction, talk through trade-offs, and follow each project from proposal to delivery.</p>
				</div>
				<p className="auth-footnote">Private by invitation. Passkey protected.</p>
			</section>
			<div className="auth-form-wrap">{children}</div>
		</main>
	);
}

function PortalLoading() {
	return (
		<main className="portal-loading" aria-busy="true" aria-label="Opening collaboration portal">
			<div className="brand-lockup">
				<span className="brand-sigil" aria-hidden="true">
					S
				</span>
				<span>
					Substrate
					<small>Collaboration Portal</small>
				</span>
			</div>
			<div className="portal-loading-line" />
			<p>Opening your workspace…</p>
		</main>
	);
}

function routeKey(route: PortalRoute) {
	return route.name === "project" ? `project:${route.id ?? ""}` : `${route.name}:${route.id ?? ""}`;
}

export function App() {
	const actor = useSyncExternalStore(subscribePortalActor, getPortalActor, getPortalActor);
	const [route, setRoute] = useState(parsePortalRoute());
	const [authResolved, setAuthResolved] = useState(() => !!actor || ["login", "claim"].includes(route.name));

	useEffect(() => {
		const change = () => {
			setRoute(parsePortalRoute());
			requestAnimationFrame(() => document.querySelector<HTMLElement>("main h1")?.focus());
		};
		addEventListener("popstate", change);
		setPortalUnauthorizedHandler(() => {
			clearPortalActor();
			setAuthResolved(true);
			navigatePortal("/login");
		});
		return () => removeEventListener("popstate", change);
	}, []);

	useEffect(() => {
		if (["login", "claim", "invite"].includes(route.name) || actor) {
			setAuthResolved(true);
			return;
		}
		let active = true;
		setAuthResolved(false);
		portalFetch<PortalMeDto>("/api/portal/v1/me")
			.then((authenticatedActor) => {
				if (!active) return;
				setPortalActor(authenticatedActor);
				setAuthResolved(true);
			})
			.catch(() => {
				if (!active) return;
				setAuthResolved(true);
				navigatePortal("/login");
			});
		return () => {
			active = false;
		};
	}, [actor, route.name]);

	function authenticated(authenticatedActor: PortalMeDto) {
		setPortalActor(authenticatedActor);
		setAuthResolved(true);
		navigatePortal("/projects");
	}

	if (route.name === "claim")
		return (
			<AuthShell>
				<InvitationView onAuthenticated={authenticated} />
			</AuthShell>
		);
	if (route.name === "invite")
		return (
			<AuthShell>
				<OrganizationInvitationView onAuthenticated={authenticated} />
			</AuthShell>
		);
	if (!authResolved) return <PortalLoading />;
	if (!actor || route.name === "login")
		return (
			<AuthShell>
				<LoginView onAuthenticated={authenticated} />
			</AuthShell>
		);

	let content: ReactNode;
	if (route.name === "projects")
		content = (
			<>
				<header className="route-heading">
					<p className="eyebrow">YOUR SHARED WORKSPACES</p>
					<h1 tabIndex={-1}>Projects in motion</h1>
					<p className="route-deck">A clear record of direction, decisions, conversations, and what comes next.</p>
				</header>
				<ProjectListView
					onSelectProject={(id) => navigatePortal(`/projects/${id}/overview`)}
					onSelectProposal={(id) => navigatePortal(`/proposals/${id}`)}
					onClaimInvitation={() => navigatePortal("/claim")}
				/>
			</>
		);
	else if (route.name === "organizations") content = <OrganizationView organizationId={route.id} />;
	else if (route.name === "project" && route.id && route.section)
		content = (
			<ProjectDetailView
				projectId={route.id}
				section={route.section}
				pushPublicKey={actor.pushPublicKey}
				previewAvailable={actor.previewAvailable}
				voiceAvailable={actor.voiceAvailable}
				agentModel={actor.agentModel}
			/>
		);
	else if (route.name === "proposal" && route.id) content = <ProposalView proposalId={route.id} />;
	else if (route.name === "inbox")
		content = (
			<>
				<header className="route-heading">
					<p className="eyebrow">PROJECT SIGNALS</p>
					<h1 tabIndex={-1}>Inbox</h1>
					<p className="route-deck">Reviews, decisions, and project updates that need your attention.</p>
				</header>
				<InboxView inboxItemId={route.id} pushPublicKey={actor.pushPublicKey} />
			</>
		);
	else content = <h1 tabIndex={-1}>Collaboration Portal</h1>;

	async function signOut() {
		if (!window.confirm("Sign out of the Collaboration Portal?")) return;
		await logoutPortal();
		navigatePortal("/login");
	}
	const projectChatActive = route.name === "project" && route.section === "conversation";
	return (
		<div className={`portal-shell${projectChatActive ? " portal-shell--project-chat" : ""}`}>
			<a className="skip-link" href="#main">
				Skip to project content
			</a>
			<aside className="portal-rail">
				<div className="brand-lockup brand-lockup-light">
					<span className="brand-sigil" aria-hidden="true">
						S
					</span>
					<span>
						Substrate
						<small>Collaboration Portal</small>
					</span>
				</div>
				<div className="rail-context">
					<span>Shared workspace</span>
					<strong>{actor.displayName}</strong>
				</div>
				<nav aria-label="Collaboration portal">
					<PortalLink href="/projects" active={route.name === "projects"}>
						Projects
					</PortalLink>
					<PortalLink href="/organizations" active={route.name === "organizations"}>
						Organizations
					</PortalLink>
					<PortalLink
						href="/inbox/all"
						active={route.name === "inbox" || (route.name === "project" && route.section === "inbox")}
					>
						Inbox
					</PortalLink>
				</nav>
				<p className="rail-note">A private channel between your team and Neal.</p>
				<AccountMenu actor={actor} onSignOut={signOut} />
			</aside>
			<main id="main">
				<div
					className={`route-stage${route.name === "project" ? " route-stage--project" : ""}${
						route.name === "organizations" ? " route-stage--organizations" : ""
					}${route.name === "project" && route.section === "conversation" ? " route-stage--discovery" : ""}`}
					key={routeKey(route)}
				>
					{content}
				</div>
			</main>
		</div>
	);
}
